import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { listSessions } from '@/api/sessions';
import { VerdictBadge } from '@/components/common/StatusBadge';
import { Loading } from '@/components/common/States';
import { relTime, shortId } from '@/lib/format';
import type { Session, SessionSummary } from '@/types';

const METRICS: { key: keyof SessionSummary; label: string }[] = [
  { key: 'rx_avg_mbps', label: 'RX avg throughput (Mbps)' },
  { key: 'rx_max_mbps', label: 'RX peak throughput (Mbps)' },
  { key: 'latency_max_ms', label: 'Max latency (ms)' },
  { key: 'latency_p95_ms', label: 'Latency p95 (ms)' },
  { key: 'latency_p99_ms', label: 'Latency p99 (ms)' },
  { key: 'loss_max_pct', label: 'Max loss (%)' },
  { key: 'jitter_max_ms', label: 'Max jitter (ms)' },
  { key: 'retransmits_total', label: 'TCP retransmits' },
];

export function Regression() {
  const { data, isLoading } = useQuery({
    queryKey: ['sessions', 'regression'],
    queryFn: () => listSessions(200),
    refetchInterval: 10_000,
  });
  const [firmware, setFirmware] = useState<string>('');
  const [metric, setMetric] = useState<keyof SessionSummary>('rx_avg_mbps');

  const firmwares = useMemo(
    () => [...new Set((data ?? []).map((s) => s.firmware).filter(Boolean) as string[])],
    [data],
  );
  const fw = firmware || firmwares[0] || '';

  const series = useMemo(() => {
    const rows = (data ?? [])
      .filter((s) => s.firmware === fw && s.summary_json && s.status === 'completed')
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
    return rows.map((s: Session, i) => ({
      i: i + 1,
      value: Number(s.summary_json?.[metric] ?? 0),
      verdict: s.verdict,
      id: s.id,
      label: s.label || shortId(s.id),
      tag: s.tag,
      created_at: s.created_at,
    }));
  }, [data, fw, metric]);

  if (isLoading) return <Loading label="Loading sessions" />;

  return (
    <div className="fade-up">
      <div className="page-head spread">
        <div>
          <div className="eyebrow">trend</div>
          <div className="title">Regression tracking</div>
          <div className="sub">A metric across every run of one firmware, oldest → newest.</div>
        </div>
        <Link to="/sessions" className="btn btn-ghost">← Sessions</Link>
      </div>

      {firmwares.length === 0 ? (
        <div className="card"><div className="dim">No firmware-tagged runs yet. Set a Firmware on the New test form.</div></div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="row wrap" style={{ gap: 18 }}>
              <div className="field" style={{ minWidth: 200 }}>
                <label className="label">Firmware</label>
                <select className="select" value={fw} onChange={(e) => setFirmware(e.target.value)}>
                  {firmwares.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div className="field grow" style={{ minWidth: 240 }}>
                <label className="label">Metric</label>
                <select className="select" value={metric} onChange={(e) => setMetric(e.target.value as keyof SessionSummary)}>
                  {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="panel" style={{ padding: '16px 16px 8px', marginBottom: 18 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              {METRICS.find((m) => m.key === metric)?.label} · {fw}
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={series} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <CartesianGrid stroke="rgba(150,180,220,0.08)" vertical={false} />
                <XAxis dataKey="i" tick={{ fill: '#3a4660', fontSize: 11 }} />
                <YAxis tick={{ fill: '#3a4660', fontSize: 11 }} width={46} />
                <Tooltip contentStyle={{ background: 'rgba(8,11,17,0.95)', border: '1px solid var(--line-2)', fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#5cc8ff"
                  strokeWidth={2}
                  isAnimationActive={false}
                  dot={(p: { cx?: number; cy?: number; payload?: { verdict?: string } }) => {
                    const v = p.payload?.verdict;
                    const color = v === 'pass' ? '#54f0a8' : v === 'fail' ? '#ff5d6c' : '#5cc8ff';
                    return <circle cx={p.cx} cy={p.cy} r={4} fill={color} stroke="none" />;
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="panel">
            <table className="table">
              <thead>
                <tr><th>#</th><th>Run</th><th>Verdict</th><th>Value</th><th>Tag</th><th>When</th></tr>
              </thead>
              <tbody>
                {series.map((r) => (
                  <tr key={r.id}>
                    <td className="mono dim">{r.i}</td>
                    <td><Link to={`/sessions/${r.id}`} className="mono">{r.label}</Link></td>
                    <td>{r.verdict ? <VerdictBadge verdict={r.verdict} /> : <span className="dim">—</span>}</td>
                    <td className="mono signal">{r.value.toFixed(2)}</td>
                    <td className="mono dim">{r.tag ? `#${r.tag}` : '—'}</td>
                    <td className="dim">{relTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
